<template>
  <div class="game-2048">
    <div class="game-header">
      <div class="header-left">
        <h2>Evolute 2048</h2>
        <p class="subtitle">Merge words & answer vocab to evolve!</p>
      </div>
      <div class="stats-panel">
        <div class="stat-box">
          <div class="stat-label">Score</div>
          <div class="stat-value">{{ score }}</div>
        </div>
        <div class="stat-box highlight">
          <div class="stat-label">Quiz Streak</div>
          <div class="stat-value">{{ quizStreak }}x</div>
        </div>
        <div class="stat-box">
          <div class="stat-label">Best Tile</div>
          <div class="stat-value">{{ bestTileLevel }}/8</div>
        </div>
      </div>
    </div>

    <!-- Game Over Overlay -->
    <transition name="fade">
      <div v-if="gameOver" class="game-over-overlay">
        <div class="game-over-content">
          <h2>{{ won ? '🎉 You Won!' : 'Game Over!' }}</h2>
          <p class="final-score">Score: {{ score }}</p>
          <p class="final-stats">Best Tile: {{ bestTileWord }}</p>
          <p class="final-stats">Quiz Accuracy: {{ quizAccuracy }}%</p>
          <button class="btn-restart" @click="startNewGame">Play Again</button>
        </div>
      </div>
    </transition>

    <div class="game-area">
      <!-- Vocabulary Quiz Panel (Left) -->
      <div class="vocab-quiz-panel">
        <h3>Vocabulary Challenge</h3>
        
        <!-- Theme Selector -->
        <div class="theme-selector">
          <label>Theme:</label>
          <select v-model="selectedThemeId" @change="changeTheme">
            <option v-for="theme in themes" :key="theme.id" :value="theme.id">
              {{ theme.name }}
            </option>
          </select>
        </div>

        <!-- Quiz Question -->
        <div class="quiz-question">
          <div class="question-text">{{ currentQuestion.question }}</div>
          <div class="question-hint">{{ currentQuestion.hint }}</div>
        </div>
        
        <!-- Answer Options -->
        <div class="answer-options">
          <button 
            v-for="(option, idx) in currentQuestion.options" 
            :key="idx"
            class="answer-btn"
            :class="{ 
              'correct': lastAnswerResult === 'correct' && option === currentQuestion.answer,
              'wrong': lastAnswerResult === 'wrong' && option === lastSelectedAnswer
            }"
            @click="selectAnswer(option)"
            :disabled="lastAnswerResult !== null"
          >
            {{ option }}
          </button>
        </div>

        <!-- Quiz Stats -->
        <div class="quiz-stats">
          <div class="stat-item">
            <span>Correct:</span>
            <span>{{ quizCorrect }}</span>
          </div>
          <div class="stat-item">
            <span>Wrong:</span>
            <span>{{ quizWrong }}</span>
          </div>
          <div class="stat-item">
            <span>Streak:</span>
            <span class="streak-highlight">{{ quizStreak }}x</span>
          </div>
        </div>

        <div class="strategy-hint">
          <strong>Tip:</strong> Correct answers add a tile to the board. Build your streak before reaching level 8 for maximum score!
        </div>
      </div>

      <!-- 2048 Board (Right) -->
      <div class="board-section">
        <div class="board-container">
          <div class="evolution-guide">
            <div class="guide-title">Evolution Chain:</div>
            <div class="evolution-chain">
              <span v-for="(word, idx) in currentTheme.chain" :key="idx" class="chain-word">
                {{ word.word }}{{ idx < currentTheme.chain.length - 1 ? ' →' : '' }}
              </span>
            </div>
          </div>

          <div class="board" @keydown="handleKeyPress" tabindex="0" ref="board">
            <!-- Background Grid -->
            <div class="grid-background">
              <div v-for="i in 16" :key="i" class="grid-cell"></div>
            </div>

            <!-- Moving Tiles -->
            <div class="tile-container">
              <div 
                v-for="tile in tiles" 
                :key="tile.id"
                class="tile"
                :class="[`tile-level-${tile.level}`, { 'tile-merged': tile.merged }]"
                :style="{ 
                  transform: `translate(${tile.x * 110}px, ${tile.y * 110}px)` 
                }"
              >
                <div class="tile-word">{{ tile.word }}</div>
                <div class="tile-level">Lv{{ tile.level }}</div>
              </div>
            </div>
          </div>

          <div class="controls-hint">
            <div>Use Arrow Keys or Swipe to move tiles</div>
            <div>Merge same words to evolve!</div>
          </div>

          <button class="btn-new-game" @click="startNewGame">New Game</button>
        </div>
      </div>
    </div>

    <!-- SEO Content -->
    <div class="seo-content-section">
      <div class="seo-scrollview">
        <div class="seo-content">
          <h2>Word Evolution 2048 - Educational Vocabulary Building Puzzle Game</h2>
          
          <h3>What is Word Evolution 2048?</h3>
          <p>Word Evolution 2048 is an innovative educational puzzle game that combines the addictive 2048 sliding tile mechanics with comprehensive vocabulary building and word progression learning. Instead of merging numbers, players merge words that evolve through logical hierarchies like biological classification (Kingdom → Phylum → Class → Order → Family → Genus → Species → Organism), plant life cycles (Seed → Sprout → Seedling → Sapling → Tree → Grove → Forest → Ecosystem), water bodies (Drop → Puddle → Stream → Creek → River → Lake → Sea → Ocean), human development stages, civilization evolution, and time scales. The game features a dual-panel design with a vocabulary quiz on the left that tests definitions, synonyms, and word knowledge, while the right panel displays the classic 4×4 2048 grid where correct quiz answers add new tiles strategically. Perfect for students expanding vocabulary, ESL learners building English proficiency, educators teaching hierarchical concepts, science students learning taxonomies, and anyone seeking intellectually stimulating puzzle gameplay that builds real knowledge.</p>
          
          <h3>How to Play Word Evolution 2048</h3>
          <p>The game runs on two simultaneous challenges. On the left, answer vocabulary questions about word definitions, meanings, and relationships - each correct answer adds a new starting tile to your 2048 board and builds your quiz streak multiplier. On the right, use arrow keys or swipe gestures to slide tiles in four directions (up, down, left, right). When two tiles with the same word touch, they merge into the next evolution level. For example, two "Seed" tiles merge into "Sprout", two "Sprout" tiles become "Seedling", continuing until you reach the final evolution. The goal is to create the highest evolution tile (level 8) while maintaining a high quiz streak. When you successfully reach level 8, your final score multiplies by your quiz streak - so a 10x streak turns 10,000 points into 100,000! Strategic players balance answering quiz questions to add tiles versus carefully planning board moves to create optimal merging opportunities. The game ends when the board fills with no possible moves remaining.</p>
          
          <h3>Educational Themes and Learning Outcomes</h3>
          <p>Word Evolution 2048 offers six comprehensive educational themes. The Biological Classification theme teaches the seven-level taxonomy hierarchy used in biology (界门纲目科属种 in Chinese) from Kingdom down to Species and individual Organisms, perfect for biology students and nature enthusiasts. The Plant Life Cycle theme follows botanical development from Seed through Sprout, Seedling, Sapling, Tree, Grove, Forest, to complete Ecosystem, teaching ecological concepts and plant biology. The Water Bodies theme demonstrates hydrological scale from Drop to Puddle, Stream, Creek, River, Lake, Sea, and Ocean, building understanding of Earth's water systems. The Human Life Stages theme explores developmental psychology through Baby, Toddler, Child, Teen, Adult, Parent, Elder, and Sage, teaching life span development. The Civilization theme traces human settlement evolution from Shelter through Hut, House, Village, Town, City, Metropolis, to Empire, perfect for history and social studies. The Time Progression theme builds temporal understanding from Second through Minute, Hour, Day, Week, Month, Year, to Decade, teaching time scale concepts essential for science and mathematics.</p>
          
          <h3>Vocabulary Quiz System and Streak Multiplier</h3>
          <p>The integrated vocabulary quiz system continuously presents questions testing word knowledge, definitions, usage, and relationships. Each theme includes comprehensive definitions and real-world examples for every word in the evolution chain. Correct answers provide dual benefits: they add a new starting tile to your board (giving you more merging opportunities) and increase your quiz streak multiplier by 1x. The streak multiplier applies when you successfully reach the final evolution tile (level 8), dramatically boosting your score. For example, reaching "Ecosystem" with a 15x quiz streak multiplies your base score by 15, rewarding both puzzle skill and vocabulary knowledge. Wrong answers reset your streak to 1x, creating strategic tension - do you guess quickly to add more tiles, or take time to ensure accuracy and maintain your multiplier? The quiz tracks accuracy percentage, correct/wrong counts, and best streak, providing measurable feedback on learning progress.</p>
          
          <h3>Strategic Gameplay and Cognitive Benefits</h3>
          <p>Word Evolution 2048 develops multiple cognitive skills simultaneously. Players practice spatial reasoning and planning (arranging tiles for optimal merges), vocabulary acquisition and retention (learning word meanings through repeated exposure), hierarchical thinking (understanding progression and classification systems), strategic decision-making (balancing quiz speed versus accuracy), working memory (tracking board state while answering questions), pattern recognition (identifying merge opportunities), and sustained attention (maintaining focus across both challenges). The game teaches that knowledge hierarchies exist across many domains - biology, ecology, sociology, history, physics - and that understanding these progressions aids comprehension and memory. Advanced strategies include corner-building (keeping highest tiles in corners), maintaining merge chains (planning several moves ahead), strategic tile placement from quiz answers, and timing quiz responses to add tiles when board space is optimal.</p>
          
          <h3>Perfect for Multiple Learning Contexts</h3>
          <p>Word Evolution 2048 excels in diverse educational settings including science classrooms teaching taxonomy and classification, ESL and language learning programs building English vocabulary, homeschool curricula needing engaging educational games, study sessions for biology, ecology, or earth science exams, vocabulary building for standardized tests (SAT, GRE, TOEFL), cognitive training programs for memory and reasoning, family educational game nights, competitive learning tournaments, and individual self-paced learning. Teachers appreciate the automatic differentiation (students progress at their own pace), built-in assessment (quiz accuracy tracking), curriculum alignment (themes match science and language standards), and observable learning outcomes. Parents value educational screen time that builds real academic knowledge, vocabulary expansion in context, no advertisements or inappropriate content, and the ability to practice independently without supervision.</p>
          
          <h3>Start Building Vocabulary Through Strategic Play</h3>
          <p>Experience the unique combination of puzzle challenge and vocabulary learning that makes Word Evolution 2048 both entertaining and educational. Whether you're a student preparing for exams, an ESL learner expanding English proficiency, a teacher seeking classroom resources, a puzzle enthusiast wanting intellectual depth, or a lifelong learner exploring new concepts, this game delivers meaningful engagement. Start playing now to master biological taxonomy, ecological systems, developmental stages, and hierarchical thinking while enjoying addictive 2048 gameplay. No registration required - just select your theme and start evolving words to build both your score and your knowledge!</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import wordEvolutionData from '../data/word_evolution_data.json';

export default {
  name: 'Game2048',
  data() {
    return {
      themes: wordEvolutionData.themes,
      selectedThemeId: 'biology_taxonomy',
      tiles: [], // Array of tile objects { id, x, y, level, word, merged }
      tileIdCounter: 0,
      score: 0,
      bestTileLevel: 0,
      gameOver: false,
      won: false,
      
      // Quiz system
      currentQuestion: { question: '', answer: '', options: [], hint: '' },
      quizStreak: 1,
      quizCorrect: 0,
      quizWrong: 0,
      lastAnswerResult: null,
      lastSelectedAnswer: null,
      
      // Touch/swipe
      touchStartX: 0,
      touchStartY: 0
    };
  },
  computed: {
    currentTheme() {
      return this.themes.find(t => t.id === this.selectedThemeId) || this.themes[0];
    },
    
    bestTileWord() {
      if (this.bestTileLevel === 0) return '-';
      return this.currentTheme.chain[this.bestTileLevel - 1].word;
    },
    
    quizAccuracy() {
      const total = this.quizCorrect + this.quizWrong;
      if (total === 0) return 100;
      return Math.round((this.quizCorrect / total) * 100);
    }
  },
  mounted() {
    this.startNewGame();
    this.$refs.board.focus();
    
    // Add touch listeners
    this.$refs.board.addEventListener('touchstart', this.handleTouchStart);
    this.$refs.board.addEventListener('touchend', this.handleTouchEnd);
  },
  beforeDestroy() {
    this.$refs.board.removeEventListener('touchstart', this.handleTouchStart);
    this.$refs.board.removeEventListener('touchend', this.handleTouchEnd);
  },
  methods: {
    startNewGame() {
      this.tiles = [];
      this.tileIdCounter = 0;
      this.score = 0;
      this.bestTileLevel = 0;
      this.gameOver = false;
      this.won = false;
      this.quizStreak = 1;
      this.quizCorrect = 0;
      this.quizWrong = 0;
      
      // Add two starting tiles
      this.addRandomTile();
      this.addRandomTile();
      
      // Generate first question
      this.generateQuestion();
    },
    
    changeTheme() {
      this.startNewGame();
    },
    
    addRandomTile() {
      const availableCells = [];
      for (let x = 0; x < 4; x++) {
        for (let y = 0; y < 4; y++) {
          if (!this.getTileAt(x, y)) {
            availableCells.push({ x, y });
          }
        }
      }
      
      if (availableCells.length > 0) {
        const { x, y } = availableCells[Math.floor(Math.random() * availableCells.length)];
        const wordData = this.currentTheme.chain[0]; // Always start with level 1
        
        this.tiles.push({
          id: this.tileIdCounter++,
          x,
          y,
          level: 1,
          word: wordData.word,
          wordZh: wordData.wordZh,
          merged: false
        });
      }
    },
    
    getTileAt(x, y) {
      return this.tiles.find(tile => tile.x === x && tile.y === y);
    },
    
    generateQuestion() {
      // Pick a random word from current theme
      const wordData = this.currentTheme.chain[Math.floor(Math.random() * this.currentTheme.chain.length)];
      
      // Create question
      const questionTypes = ['definition', 'example'];
      const type = questionTypes[Math.floor(Math.random() * questionTypes.length)];
      
      if (type === 'definition') {
        this.currentQuestion.question = `What does "${wordData.word}" mean?`;
        this.currentQuestion.answer = wordData.definition;
        this.currentQuestion.hint = `Level ${wordData.level} in ${this.currentTheme.name}`;
        
        // Generate wrong answers from other words
        const wrongAnswers = this.currentTheme.chain
          .filter(w => w.word !== wordData.word)
          .map(w => w.definition)
          .sort(() => Math.random() - 0.5)
          .slice(0, 4);
        
        this.currentQuestion.options = [wordData.definition, ...wrongAnswers]
          .sort(() => Math.random() - 0.5);
      } else {
        this.currentQuestion.question = `Which word matches this definition: "${wordData.definition}"?`;
        this.currentQuestion.answer = wordData.word;
        this.currentQuestion.hint = wordData.example;
        
        // Generate wrong answers
        const wrongAnswers = this.currentTheme.chain
          .filter(w => w.word !== wordData.word)
          .map(w => w.word)
          .sort(() => Math.random() - 0.5)
          .slice(0, 4);
        
        this.currentQuestion.options = [wordData.word, ...wrongAnswers]
          .sort(() => Math.random() - 0.5);
      }
    },
    
    selectAnswer(option) {
      if (this.lastAnswerResult !== null || this.gameOver) return;
      
      this.lastSelectedAnswer = option;
      const isCorrect = option === this.currentQuestion.answer;
      
      if (isCorrect) {
        this.quizCorrect++;
        this.quizStreak++;
        this.lastAnswerResult = 'correct';
        
        // Add a tile to the board
        this.addRandomTile();
      } else {
        this.quizWrong++;
        this.quizStreak = 1;
        this.lastAnswerResult = 'wrong';
      }
      
      // Clear feedback and generate new question
      setTimeout(() => {
        this.lastAnswerResult = null;
        this.lastSelectedAnswer = null;
        this.generateQuestion();
      }, 800);
    },
    
    handleKeyPress(e) {
      if (this.gameOver) return;
      
      switch(e.key) {
        case 'ArrowUp':
          e.preventDefault();
          this.move({ x: 0, y: -1 });
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.move({ x: 0, y: 1 });
          break;
        case 'ArrowLeft':
          e.preventDefault();
          this.move({ x: -1, y: 0 });
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.move({ x: 1, y: 0 });
          break;
      }
    },
    
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },
    
    handleTouchEnd(e) {
      if (this.gameOver) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const dx = touchEndX - this.touchStartX;
      const dy = touchEndY - this.touchStartY;
      
      const minSwipeDistance = 50;
      
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > minSwipeDistance) {
        // Horizontal swipe
        this.move({ x: dx > 0 ? 1 : -1, y: 0 });
      } else if (Math.abs(dy) > minSwipeDistance) {
        // Vertical swipe
        this.move({ x: 0, y: dy > 0 ? 1 : -1 });
      }
    },
    
    move(vector) {
      let moved = false;
      const traversals = this.buildTraversals(vector);
      
      // Reset merge status
      this.tiles.forEach(tile => tile.merged = false);
      
      traversals.x.forEach(x => {
        traversals.y.forEach(y => {
          const tile = this.getTileAt(x, y);
          if (tile) {
            const positions = this.findFarthestPosition({ x, y }, vector);
            const next = this.getTileAt(positions.next.x, positions.next.y);
            
            if (next && next.level === tile.level && !next.merged) {
              // Merge
              const newLevel = tile.level + 1;
              if (newLevel <= this.currentTheme.chain.length) {
                // Move tile to position
                tile.x = positions.next.x;
                tile.y = positions.next.y;
                
                // Mark for removal/update
                this.mergeTiles(tile, next, newLevel);
                moved = true;
              }
            } else {
              // Move
              if (tile.x !== positions.farthest.x || tile.y !== positions.farthest.y) {
                tile.x = positions.farthest.x;
                tile.y = positions.farthest.y;
                moved = true;
              }
            }
          }
        });
      });
      
      if (moved) {
        setTimeout(() => {
          this.addRandomTile();
          this.checkGameOver();
        }, 200); // Wait for animation
      }
    },
    
    mergeTiles(source, target, newLevel) {
      // Remove both tiles and create a new one
      // We wait a bit to let the slide animation finish visually
      setTimeout(() => {
        this.tiles = this.tiles.filter(t => t.id !== source.id && t.id !== target.id);
        
        const wordData = this.currentTheme.chain[newLevel - 1];
        this.tiles.push({
          id: this.tileIdCounter++,
          x: target.x,
          y: target.y,
          level: newLevel,
          word: wordData.word,
          wordZh: wordData.wordZh,
          merged: true // Flag for pop animation
        });
        
        this.score += newLevel * 100;
        if (newLevel > this.bestTileLevel) {
          this.bestTileLevel = newLevel;
        }
        
        if (newLevel === this.currentTheme.chain.length) {
          this.handleWin();
        }
      }, 150);
    },
    
    buildTraversals(vector) {
      const traversals = { x: [], y: [] };
      for (let pos = 0; pos < 4; pos++) {
        traversals.x.push(pos);
        traversals.y.push(pos);
      }
      
      // Always traverse from the farthest cell in the chosen direction
      if (vector.x === 1) traversals.x = traversals.x.reverse();
      if (vector.y === 1) traversals.y = traversals.y.reverse();
      
      return traversals;
    },
    
    findFarthestPosition(cell, vector) {
      let previous;
      
      // Progress towards the vector direction until an obstacle is found
      do {
        previous = cell;
        cell = { x: previous.x + vector.x, y: previous.y + vector.y };
      } while (
        this.withinBounds(cell) &&
        !this.getTileAt(cell.x, cell.y)
      );
      
      return {
        farthest: previous,
        next: cell // Used to check if a merge is possible
      };
    },
    
    withinBounds(position) {
      return position.x >= 0 && position.x < 4 &&
             position.y >= 0 && position.y < 4;
    },
    
    checkGameOver() {
      if (this.tiles.length < 16) return; // Empty space exists
      
      // Check for possible merges
      for (let x = 0; x < 4; x++) {
        for (let y = 0; y < 4; y++) {
          const tile = this.getTileAt(x, y);
          if (tile) {
            const neighbors = [
              { x: x + 1, y: y },
              { x: x, y: y + 1 }
            ];
            
            for (const pos of neighbors) {
              if (this.withinBounds(pos)) {
                const neighbor = this.getTileAt(pos.x, pos.y);
                if (neighbor && neighbor.level === tile.level) {
                  return; // Merge possible
                }
              }
            }
          }
        }
      }
      
      this.gameOver = true;
    },
    
    handleWin() {
      this.won = true;
      this.score *= this.quizStreak;
      setTimeout(() => {
        this.gameOver = true;
      }, 1000);
    }
  }
};
</script>

<style scoped>
.game-2048 {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  /* Updated background to dark purple */
  background: linear-gradient(135deg, #523479 0%, #2b1055 100%);
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 1400px;
  margin: 0 auto;
  user-select: none;
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

.header-left h2 {
  margin: 0;
  color: white;
  font-size: 32px;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.subtitle {
  margin: 5px 0 0 0;
  color: rgba(255,255,255,0.9);
  font-size: 14px;
}

.stats-panel {
  display: flex;
  gap: 15px;
}

.stat-box {
  background: rgba(255,255,255,0.2);
  padding: 10px 15px;
  border-radius: 10px;
  text-align: center;
  min-width: 80px;
  backdrop-filter: blur(10px);
}

.stat-box.highlight {
  background: rgba(255,215,0,0.3);
  border: 2px solid rgba(255,215,0,0.5);
}

.stat-label {
  font-size: 12px;
  color: rgba(255,255,255,0.8);
  text-transform: uppercase;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: white;
  margin-top: 5px;
}

.game-over-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.game-over-content {
  background: white;
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.3);
}

.game-over-content h2 {
  margin: 0 0 20px 0;
  color: #523479;
  font-size: 36px;
}

.final-score, .final-stats {
  font-size: 20px;
  margin: 10px 0;
  color: #2c3e50;
}

.btn-restart {
  margin-top: 20px;
  padding: 15px 30px;
  background: #523479;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-restart:hover {
  background: #6a439c;
  transform: scale(1.05);
}

.game-area {
  display: flex;
  gap: 30px;
  width: 100%;
  max-width: 1200px;
}

/* Vocabulary Quiz Panel */
.vocab-quiz-panel {
  flex: 0 0 350px;
  background: rgba(255,255,255,0.15);
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.vocab-quiz-panel h3 {
  margin: 0;
  color: white;
  font-size: 24px;
  text-align: center;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
}

.theme-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.theme-selector label {
  color: white;
  font-weight: bold;
}

.theme-selector select {
  padding: 10px;
  border-radius: 8px;
  border: none;
  background: rgba(255,255,255,0.9);
  font-size: 14px;
  cursor: pointer;
  color: #333;
}

.quiz-question {
  background: rgba(0,0,0,0.3);
  padding: 20px;
  border-radius: 10px;
}

.question-text {
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin-bottom: 10px;
}

.question-hint {
  font-size: 14px;
  color: rgba(255,255,255,0.7);
  font-style: italic;
}

.answer-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.answer-btn {
  padding: 15px;
  font-size: 16px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s;
  text-align: left;
}

.answer-btn:hover:not(:disabled) {
  transform: translateX(5px);
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
}

.answer-btn.correct {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  animation: correctPulse 0.5s;
}

.answer-btn.wrong {
  background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
  animation: wrongShake 0.5s;
}

@keyframes correctPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes wrongShake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-10px); }
  75% { transform: translateX(10px); }
}

.quiz-stats {
  background: rgba(0,0,0,0.2);
  padding: 15px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.quiz-stats .stat-item {
  display: flex;
  justify-content: space-between;
  color: white;
  font-size: 16px;
}

.streak-highlight {
  color: #ffd700 !important;
  font-weight: bold;
}

.strategy-hint {
  background: rgba(255,215,0,0.2);
  padding: 15px;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  line-height: 1.5;
  border: 2px solid rgba(255,215,0,0.3);
}

/* Board Section */
.board-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.board-container {
  background: rgba(255,255,255,0.15);
  padding: 20px;
  border-radius: 15px;
  backdrop-filter: blur(10px);
}

.evolution-guide {
  margin-bottom: 15px;
  background: rgba(0,0,0,0.2);
  padding: 15px;
  border-radius: 10px;
}

.guide-title {
  color: white;
  font-weight: bold;
  margin-bottom: 10px;
}

.evolution-chain {
  color: rgba(255,255,255,0.9);
  font-size: 14px;
  line-height: 1.8;
}

.chain-word {
  display: inline-block;
  margin-right: 5px;
}

.board {
  background: rgba(0,0,0,0.3);
  border-radius: 10px;
  padding: 10px;
  width: 460px; /* 4 * 110 + 20 padding */
  height: 460px;
  position: relative;
  outline: none;
}

.grid-background {
  display: grid;
  grid-template-columns: repeat(4, 100px);
  grid-template-rows: repeat(4, 100px);
  gap: 10px;
  position: absolute;
}

.grid-cell {
  width: 100px;
  height: 100px;
  background: rgba(255,255,255,0.2);
  border-radius: 8px;
}

.tile-container {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 440px;
  height: 440px;
}

.tile {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: transform 0.15s ease-in-out;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  z-index: 10;
}

.tile-merged {
  animation: pop 0.2s ease-in-out;
  z-index: 20;
}

@keyframes pop {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

.tile-word {
  font-size: 16px;
  font-weight: bold;
  color: white;
  text-align: center;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
  padding: 0 5px;
  word-wrap: break-word;
}

.tile-level {
  font-size: 12px;
  color: rgba(255,255,255,0.8);
  margin-top: 5px;
}

/* Tile colors by level */
.tile-level-1 { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.tile-level-2 { background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); }
.tile-level-3 { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); }
.tile-level-4 { background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%); }
.tile-level-5 { background: linear-gradient(135deg, #fa709a 0%, #fee140 100%); }
.tile-level-6 { background: linear-gradient(135deg, #30cfd0 0%, #330867 100%); }
.tile-level-7 { background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%); }
.tile-level-8 { background: linear-gradient(135deg, #ff9a56 0%, #ff6a88 100%); animation: pulse 2s infinite; }

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.controls-hint {
  margin-top: 15px;
  text-align: center;
  color: white;
  font-size: 14px;
  line-height: 1.6;
}

.btn-new-game {
  width: 100%;
  padding: 15px;
  margin-top: 15px;
  background: rgba(255,255,255,0.3);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-new-game:hover {
  background: rgba(255,255,255,0.4);
  transform: translateY(-2px);
}

/* SEO Content */
.seo-content-section {
  width: 100%;
  margin-top: 30px;
}

.seo-scrollview {
  max-height: 300px;
  overflow-y: auto;
  background: rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 20px;
  backdrop-filter: blur(10px);
}

.seo-content {
  color: white;
  line-height: 1.8;
}

.seo-content h2 {
  margin-top: 0;
  color: white;
  font-size: 24px;
}

.seo-content h3 {
  color: rgba(255,255,255,0.95);
  font-size: 18px;
  margin-top: 20px;
}

.seo-content p {
  margin: 10px 0;
  color: rgba(255,255,255,0.9);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
